import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, makeStyles, MenuItem, TextField, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import CloseIcon from '@material-ui/icons/Close';
import { useEffect, useState } from "react";
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import MapView from "../MapView";
import { getMapCenterByCityId } from "../../utilities/map";
import { getMapCenterByCityIdUS } from "../../utilities/mapUS";
import { getUserPreference, latLngVerify, regexZipMatch } from "../../utilities/common";
import { Marker } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { addressAutocorrection, getAddress, updateOrCreateAddress } from "../../actions/wrongAddressActions";
import FullscreenLoading from "../Common/FullscreenLoading";
import { showAlertSnackbar } from "../../reducers/uiSlice";
import _ from "lodash";
import { getProvinceCountryByWarehouse } from "../../actions/driverAppApprovalActions";
import { useToken } from "../../utilities/hooks";

function CorrectionModal(props) {
    const {t} = useTranslation('wrongAddressTool');
    const dispatch = useDispatch();

    const tokenContents = useToken();
    const userUS = tokenContents.userUS;

    const userPref = getUserPreference();

    const REQUIRED_ELEMENTS = [
        'address',
        'postal_code',
        'new_address',
        'new_postal_code',
        'lat',
        'lng',
        'street_number',
        'route',
        'locality',
        'area_level_1',
        'country'  
    ]

    const INITIAL_PARAMS = {
        address: '',
        postal_code: '',
        new_address: '',
        new_postal_code: '',
        lat: 0,
        lng: 0,
        street_number: '',
        route: '',
        area_level_2: null,
        area_level_1: '',
        country: '',
        addr_type: '-1',
        notes: '',
        locality: '',
        buzz_code: '',
        unit_no: '',
        operator: userPref.username,
        update_order_only: false
    }


    const addressTypeList = useSelector(state=>state.wrongAddressReducer.getExistAddressTypes);
    const countryProvince = useSelector(state=>state.driverAppApproval.getProvinceCountryByWarehouse);
    const provinceList = useSelector(state=>state.wrongAddressReducer.getProvincesCovered);

    const [correctiveParams, setCorrectiveParams] = useState(INITIAL_PARAMS)
    const [loading, setLoading] = useState(false);



    const handleParams = (element, value) => {
        setCorrectiveParams((prev) => ({
            ...prev,
            [element]: value
        }))
    }

    const handleMapClick = (event) => {
        handleParams('lat', event.latLng.lat());
        handleParams('lng', event.latLng.lng());
    }

    const paramKeyTransfer = (key) => {
        switch(key){
            case "original_address":
                return 'address'
            case "original_postal_code":
                return 'postal_code'
            case "corrected_addr":
                return 'new_address'
            case "format_address":
                return 'new_address'
            case "postal_code_short":
                return 'new_postal_code'
            case "route_short":
                return 'route'
            case "country_short":
                return 'country'
            case "administrative_level_1_short":
                return 'area_level_1'
            case "province":
                return 'area_level_1'
            case "city":
                return 'locality'
            case "street_name":
                return 'route'
            case "district":
                return 'area_level_2'
            default: 
                return key
        }
    }

    const handleAssignCorrection = (data, disabled) => {
        _.map(data, (value, key) => {
            if(!(
                (key === 'original_address' || key === 'original_postal_code')
                && 
                (props.type === 'new' || props.isExist || disabled)
            )) {
                handleParams(paramKeyTransfer(key), value);
            }
        })
    }

    const handleGeoCorrection = async(type) => {
        setLoading(true);
        try {
            if(
                type === 'original' ? 
                (!correctiveParams['address'] || !correctiveParams['postal_code']) 
                : 
                (!correctiveParams['new_address'] || !correctiveParams['new_postal_code'])
            ) {
                dispatch(showAlertSnackbar({message:
                    type === 'original' ? 
                    t('original_address_postal_code_required') :
                    t('address_postal_code_required'), type: 'error'}))
                return;
            }
            let params = {
                address: type === 'original' ? correctiveParams['address'] : correctiveParams['new_address'],
                postal_code: type === 'original' ? correctiveParams['postal_code'] : correctiveParams['new_postal_code']
            }
            let response = await dispatch(addressAutocorrection(params));
            if(!response.is_invalid_address) {
                await handleAssignCorrection(response, props.type === 'batch');
            } else {
                dispatch(showAlertSnackbar({message:t('invalid_address'),type:'error'}))
            }
        } catch(err) {
            dispatch(showAlertSnackbar({message:err.message,type:'error'}))
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async() => {
        if(verifyRequired(correctiveParams) && latLngFormatVerify(correctiveParams)) {
            try {
                setLoading(true);
                let searchParams = {
                    address: correctiveParams.address,
                    postal_code: correctiveParams.postal_code
                };
                if(props.isExist) {
                    await dispatch(updateOrCreateAddress(correctiveParams));
                    await props.handleSearch();
                    await props.onClose();
                } else {
                    await dispatch(getAddress(searchParams));
                    await dispatch(updateOrCreateAddress(correctiveParams));
                    await dispatch(getAddress(searchParams));
                }
            } catch(err) {
                dispatch(showAlertSnackbar({message:err.message,type:'error'}))
            } finally {
                setLoading(false);
            }
        }
    }

    const latLngFormatVerify = () => {
        if(!latLngVerify(correctiveParams)) {
            dispatch(showAlertSnackbar({message: t('common:lat_lng_invalid'), type: 'warning'}))
            return false;
        }
        return true;
    }

    const handleSaveClose = () => {
        if(verifyRequired(correctiveParams) && latLngFormatVerify(correctiveParams)) {
            props.handleUpdateClose(correctiveParams, 'add');
        }
    }

    const handleComplete = async() => {
        if(verifyRequired(correctiveParams) && latLngFormatVerify(correctiveParams)) {
            props.handleUpdateClose(correctiveParams, 'delete');
        }
    }
    const verifyRequired = (params) => {
        for(var ele in REQUIRED_ELEMENTS) {
            console.log(REQUIRED_ELEMENTS[ele])
            if(!params[REQUIRED_ELEMENTS[ele]]) {
                dispatch(showAlertSnackbar({message:t('all_required'),type:'error'}));
                return false;
            } else if (REQUIRED_ELEMENTS[ele] === 'new_postal_code') {
                if (!regexZipMatch(params['new_postal_code'], userUS)) {
                    dispatch(showAlertSnackbar({ message: t('zip_regex'), type: 'error' }));
                    return false;
                }
            }
        }
        return true;
    }



    useEffect(() => {
        setCorrectiveParams(INITIAL_PARAMS);
        if(props.open && props.type === 'new') {
            let coordinate = userUS ? getMapCenterByCityIdUS(userPref.userCity) : getMapCenterByCityId(userPref.userCity);
            handleParams('lat', coordinate.lat);
            handleParams('lng', coordinate.lng);
        }
    }, [props.open])

    useEffect(() => {
        if(countryProvince.length > 0) {
            const countryObj = countryProvince.find(element => element.level === 0);
            handleParams('country', countryObj.code);
        } else {
            dispatch(getProvinceCountryByWarehouse(userPref.userCity))
        }
    }, [countryProvince, props.open])

    useEffect(() => {
        if(props.type !== 'new') {
            handleAssignCorrection(props.data);
        }
    }, [props.type, props.data])



    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            disableBackdropClick={true}
            maxWidth='lg'
            fullWidth
        >
            <DialogTitle>
                {props.type === 'new' ? t('new_correction') : t('correction_details')}
                <Tooltip title={t('common:cancel')} aria-label="cancel" style={{float: "right"}}>
                    <CloseIcon onClick={props.onClose}></CloseIcon>
                </Tooltip> 
            </DialogTitle>
            <DialogContent>
                <Grid container alignItems="center" justify="center" spacing={4}>
                    <Grid item xs={6} container spacing={3} alignItems="center" justify="center" style={{width:'100vw', height: '80vh'}}>
                        <Grid item xs={12}>
                            {t('original_address')}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('original_address_star')}
                                size="small"
                                id="wrongaddressmanagement-original-address-textfield"
                                variant="outlined"
                                value={correctiveParams['address']}
                                onChange={(e)=>handleParams('address', e.target.value)}
                                fullWidth
                                disabled={props.type !== 'new'}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('original_postal_code_star')}
                                size="small"
                                id="wrongaddressmanagement-original-postal-code-textfield"
                                variant="outlined"
                                value={correctiveParams['postal_code']}
                                onChange={(e)=>handleParams('postal_code', e.target.value)}
                                fullWidth
                                disabled={props.type !== 'new'}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Button 
                                color="primary"
                                id="wrongaddressmanagement-original-correct-button"
                                onClick={()=>handleGeoCorrection('original')}
                            >
                                {t('correct')}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            {t('corrective_address')}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('address_star')}
                                size="small"
                                id="wrongaddressmanagement-address-textfield"
                                variant="outlined"
                                value={correctiveParams['new_address']}
                                onChange={(e)=>handleParams('new_address', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('postal_code_star')}
                                size="small"
                                id="wrongaddressmanagement-postal-code-textfield"
                                variant="outlined"
                                value={correctiveParams['new_postal_code']}
                                onChange={(e)=>handleParams('new_postal_code', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <img
                                src='/images/icons/address-correction.svg'
                                id="wrongaddressmanagement-correct-button"
                                style={{cursor:'pointer'}}
                                onClick={()=>handleGeoCorrection('new')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label={t('street_number_star')}
                                size="small"
                                id="wrongaddressmanagement-street-number-textfield"
                                variant="outlined"
                                value={correctiveParams['street_number']}
                                onChange={(e)=>handleParams('street_number', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label={t('unit_number')}
                                size="small"
                                id="wrongaddressmanagement-unit-number-textfield"
                                variant="outlined"
                                value={correctiveParams['unit_no']}
                                onChange={(e)=>handleParams('unit_no', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('street_name_star')}
                                size="small"
                                id="wrongaddressmanagement-street-name-textfield"
                                variant="outlined"
                                value={correctiveParams['route']}
                                onChange={(e)=>handleParams('route', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('country_star')}
                                size="small"
                                id="wrongaddressmanagement-country-textfield"
                                variant="outlined"
                                value={correctiveParams['country']}
                                onChange={(e)=>handleParams('country', e.target.value)}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('province_star')}
                                size="small"
                                id="wrongaddressmanagement-province-textfield"
                                variant="outlined"
                                value={correctiveParams['area_level_1']}
                                onChange={(e)=>handleParams('area_level_1', e.target.value)}
                                fullWidth
                                select
                            >
                            {
                                provinceList?.length > 0 &&
                                provinceList.map((province) => {
                                    return <MenuItem key={province.code} value={province.code}>{province.code}</MenuItem>
                                })
                            }
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('city_star')}
                                size="small"
                                id="wrongaddressmanagement-city-textfield"
                                variant="outlined"
                                value={correctiveParams['locality']}
                                onChange={(e)=>handleParams('locality', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('district')}
                                size="small"
                                id="wrongaddressmanagement-district-textfield"
                                variant="outlined"
                                value={correctiveParams['area_level_2']}
                                onChange={(e)=>handleParams('area_level_2', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('buzz_code')}
                                size="small"
                                id="wrongaddressmanagement-buzz-code-textfield"
                                variant="outlined"
                                value={correctiveParams['buzz_code']}
                                onChange={(e)=>handleParams('buzz_code', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label={t('address_type')}
                                size="small"
                                id="wrongaddressmanagement-address-type-textfield"
                                variant="outlined"
                                value={correctiveParams['addr_type']}
                                onChange={(e)=>handleParams('addr_type', e.target.value)}
                                fullWidth
                            >
                                {
                                    _.map(addressTypeList, (content, key) => {
                                        return (
                                            <MenuItem value={Number(key)}>{content}</MenuItem>
                                        )
                                    })
                                }
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('latitude_star')}
                                size="small"
                                id="wrongaddressmanagement-latitude-textfield"
                                variant="outlined"
                                value={correctiveParams['lat']}
                                onChange={(e)=>handleParams('lat', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('longitude_star')}
                                size="small"
                                id="wrongaddressmanagement-longitude-textfield"
                                variant="outlined"
                                value={correctiveParams['lng']}
                                onChange={(e)=>handleParams('lng', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={6} style={{width:'100vw', height: '80vh'}}>
                        <MapView
                            center={userUS ? getMapCenterByCityIdUS(userPref.userCity) : getMapCenterByCityId(userPref.userCity)}
                            id="wrongaddressmanagement-mapView"
                            mapStyle={'light'}
                            onClick={handleMapClick}
                        >
                            <Marker
                                options={{
                                    position: {
                                        lat: Number(correctiveParams['lat']),
                                        lng: Number(correctiveParams['lng'])
                                    },
                                    icon: "./images/markers/marker.png"
                                }}
                            />
                        </MapView>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                style={!(props.type === 'new' || props.type === 'detail') ? {
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                } : {}}
            >
                {
                    !(props.type === 'new' || props.type === 'detail') &&
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                                id='wrongaddressmanagement-correction-isonetime-checkbox'
                                color='secondary'
                                checked={correctiveParams['update_order_only']}
                                onChange={(e) => {
                                    handleParams('update_order_only', e.target.checked)
                                }}
                        />
                        <span style={{ marginRight: 4, color: 'red' }}>{t('update_one_time_only')}</span>
                        <span>{t('keep_original_stored')}</span>
                    </div>
                }
                <div>
                    {
                        (props.type === 'new' || props.type === 'detail') ?
                            <Button
                                color='primary'
                                variant='contained'
                                style={{ margin: 30 }}
                                onClick={handleSave}
                                id="wrongaddressmanagement-createorsave-button"
                            >
                                {props.type === 'new' ? t('common:create') : t('common:save')}
                            </Button>
                            :
                            <div>
                                {
                                    !props.isEditOrder &&
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        style={{ margin: 30 }}
                                        onClick={handleSaveClose}
                                        id="wrongaddressmanagement-saveandclose-button"
                                    >
                                        {t('save_close')}
                                    </Button>
                                }
                                <Button
                                    color='primary'
                                    variant='contained'
                                    style={{ margin: 30 }}
                                    onClick={handleComplete}
                                    id="wrongaddressmanagement-completecorrection-button"
                                >
                                    {t('complete_correction')}
                                </Button>
                            </div>
                    }
                </div>
            </DialogActions>
            <FullscreenLoading open={loading} />
        </Dialog>
    )
}

export default CorrectionModal;